import { cn } from '@/constants/design-tokens';
import { Toggle } from '@bbpos/ui';

const TIME_INPUT_CLASS = cn(
  'w-full h-10 px-3 rounded-lg bg-white dark:bg-neutral-900 text-sm font-mono tabular-nums',
  'text-neutral-900 dark:text-neutral-100',
  'ring-1 ring-neutral-200 dark:ring-neutral-700',
  'focus:outline-none focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400',
  'transition-shadow',
);

const DAYS: { value: number; short: string; full: string }[] = [
  { value: 1, short: 'Mon', full: 'Monday' },
  { value: 2, short: 'Tue', full: 'Tuesday' },
  { value: 3, short: 'Wed', full: 'Wednesday' },
  { value: 4, short: 'Thu', full: 'Thursday' },
  { value: 5, short: 'Fri', full: 'Friday' },
  { value: 6, short: 'Sat', full: 'Saturday' },
  { value: 0, short: 'Sun', full: 'Sunday' },
];

interface ScheduleFieldsProps {
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  onDaysChange: (days: number[]) => void;
}

export function ScheduleFields({
  startTime,
  endTime,
  daysOfWeek,
  onStartTimeChange,
  onEndTimeChange,
  onDaysChange,
}: ScheduleFieldsProps) {
  const isRestricted = daysOfWeek.length > 0;

  const toggleDay = (day: number) => {
    const next = daysOfWeek.includes(day)
      ? daysOfWeek.filter((d) => d !== day)
      : [...daysOfWeek, day].sort((a, b) => a - b);
    onDaysChange(next);
  };

  const handleRestrictToggle = (on: boolean) => {
    // Off → every day (empty array). On → seed with Mon-Fri if nothing selected.
    if (on) {
      if (daysOfWeek.length === 0) onDaysChange([1, 2, 3, 4, 5]);
    } else {
      onDaysChange([]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
            Start Time (optional)
          </label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => onStartTimeChange(e.target.value)}
            className={TIME_INPUT_CLASS}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">
            End Time (optional)
          </label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => onEndTimeChange(e.target.value)}
            className={TIME_INPUT_CLASS}
          />
        </div>
      </div>

      <div>
        <label className="flex items-center justify-between gap-3 cursor-pointer mb-2">
          <div>
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Restrict to specific days
            </p>
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
              Off — rule is active every day within the date range
            </p>
          </div>
          <Toggle checked={isRestricted} onChange={handleRestrictToggle} />
        </label>

        {isRestricted && (
          <>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((day) => {
                const selected = daysOfWeek.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    aria-pressed={selected}
                    title={day.full}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-150 select-none',
                      selected
                        ? 'bg-gradient-to-br from-warning-500 to-danger-500 text-white shadow-[0_6px_16px_-6px_rgba(245,158,11,0.6)]'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700',
                    )}
                  >
                    {day.short}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-2">
              {daysOfWeek.length === 0
                ? 'Select at least one day.'
                : `Active only on ${daysOfWeek
                    .map((d) => DAYS.find((x) => x.value === d)?.short)
                    .filter(Boolean)
                    .join(', ')}.`}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
